/**
 * Evergreen Valley College brand colors (W54 Worker E, S3 — theme).
 *
 * Source status (driver ruling 2026-10-21, option (c); fact sheet brand.*): the ONLY hex on any
 * robots-allowed first-party page is #1D8649, the fill of the catalog footer's social-media icons
 * (catalog.evc.edu home page, inline SVG). Both hosts disallow their stylesheets and logo assets
 * to crawlers (catalog /css/ /images/; www.evc.edu /_resources/), and the evc.edu sitemap has no
 * brand page, so no second first-party source was read (doc 51 S0-7). The primary is therefore
 * `reported`, the accent is a NEUTRAL chosen for contrast and marked `assumed`; this file is
 * replaced the day the owner supplies EVC's brand guide (flagged in doc 54 §2).
 *
 * Contrast (WCAG, measured 2026-10-21 against the token surfaces in index.css — light card
 * #FFFFFF / paper #F5F1EB, dark ground #161412 / dark card #1F1C19):
 *   #1D8649 (29 134 73, the primary): 4.61 card / 4.09 paper / 3.99 dark ground / 3.68 dark card
 *     — BELOW the 4.5 text floor on paper and on both dark surfaces; white text on it 4.61:1.
 *   `-strong` #145E33 (20 94 51): white text 7.84:1.
 *   A green TEXT tier is not a ramp step. The token system has no separate primary text tier
 *   (`text-primary` and `bg-primary` both resolve to `--color-primary`, and a theme carries no
 *   dark-mode values), so the fix for the sub-4.5 text above is the platform `--color-primary-fg`
 *   token (light 23 107 58 / dark 74 190 120, clearing 4.5 on all four surfaces) — the driver's
 *   synthesis item (ruling 2026-10-21, option (c); onboarding log §3). The primary stays #1D8649.
 *   An earlier draft parked that darker text value at the 600 step, which made the ramp
 *   non-monotonic (600 darker than 700); #470 review restored 600 to the HSL ladder (no component
 *   uses `primary-600`, so no rendered contrast changed).
 *   Accent #5B6472 (91 100 114), a slate neutral: 5.98:1 on the card, 5.32:1 on paper — clears
 *     4.5 on BOTH light surfaces as text; on the dark surfaces 3.07:1 (#161412) / 2.83:1
 *     (#1F1C19) — the theme system has no dark-mode accent token (doc 51 S3-6; De Anza, SDSU and
 *     Foothill record the same).
 *
 * Brand mark (components/ui/BrandMark.tsx; every front face >= 50 RGB from every other
 * institution's, measured against umd, sjsu, sdsu, ucmerced, deanza, merced-college, foothill,
 * montgomery-college and unic fronts in both modes): the GREEN space is free —
 *   light front = the primary 29 134 73 (min distance 99.2, to UC Merced's navy);
 *   dark front  = the 400-ish tint 74 190 120 (min distance 135.1, to SJSU's blue).
 * Ramps are HSL ladders pinned at the brand value as the 700 step and DEFAULT (the SJSU
 * derivation; EVC publishes no extended palette): primary hue 145, saturation ~64.5%, lightness
 * 97 / 89 / 81 / 71 / 62 / 52 / 42 / 32 (brand) / 22 / 13 for 50-900, monotonic.
 * Text-only branding, no logo asset embedded.
 */
import type { ThemeColors } from '../types';

export const evcColors: ThemeColors = {
  // Primary — the measured green (#1D8649 = rgb(29, 134, 73)); -strong = #145E33.
  '--color-primary':     '29 134 73',
  '--color-primary-strong': '20 94 51',
  '--color-primary-50':  '242 252 247',
  '--color-primary-100': '210 245 225',
  '--color-primary-200': '174 237 200',
  '--color-primary-300': '135 229 174',
  '--color-primary-400': '95 220 148',
  '--color-primary-500': '54 212 120',
  '--color-primary-600': '38 176 96',
  '--color-primary-700': '29 134 73',
  '--color-primary-800': '20 94 51',
  '--color-primary-900': '12 54 29',

  // Accent — a slate NEUTRAL (#5B6472 = rgb(91, 100, 114)), `assumed`; see header.
  '--color-accent':     '91 100 114',
  '--color-accent-50':  '246 247 249',
  '--color-accent-100': '224 228 233',
  '--color-accent-200': '199 205 215',
  '--color-accent-300': '173 182 197',
  '--color-accent-400': '146 158 177',
  '--color-accent-500': '118 133 158',
  '--color-accent-600': '95 109 133',
  '--color-accent-700': '91 100 114',
  '--color-accent-800': '52 59 73',
  '--color-accent-900': '30 34 42',

  // Brand-mark faces. Light: the green front, two darker oblique steps. Dark: a lighter green
  // front, two darker steps. Distances in the header.
  '--color-mark-front': '29 134 73',
  '--color-mark-top':   '20 94 51',
  '--color-mark-side':  '12 54 29',
  '--color-mark-front-dm': '74 190 120',
  '--color-mark-top-dm':   '38 177 96',
  '--color-mark-side-dm':  '29 134 73',
};
